const {clamp} = require("./ElevHelpers");
const {setupScheduleDemoModal} = require("./ScheduleDemoModalUtil");

/**
 * Attaches a listener to events that affect the position of elements.
 * Also executes the listener once.
 *
 * @param {Function} fn
 * @param {Object} opts
 * 	- watchHeader: whether to listen to size changes to the header
 */
const runAndAttachPageListeners = (fn, opts = {}) => {
	fn();
	window.addEventListener("resize", fn);
	document.addEventListener("scroll", fn);
	if (opts.watchHeader) {
		const header = document.querySelector(".ba11-top");
		const resizeObserver = new ResizeObserver(fn);
		resizeObserver.observe(header);
	}
};

/*********
 * SETUP *
 *********/

const setupRocket = () => {
	const el = document.querySelector("#outcomes-blastoff-rocket-desktop");
	if (!el) return;

	const animateElement = () => {
		const rect = el.getBoundingClientRect();
		if (!rect.width) return;

		const parentEl = el.parentElement;
		const parentRect = parentEl.getBoundingClientRect();
		const startY = (parentRect.height * 2) / 3;
		const finalY = 0;
		const {innerHeight: windowHeight} = window;
		const targetTop = windowHeight / 5; // 1/5 down the window
		const deltaY = finalY - startY;

		const calculatedTop =
			(targetTop - parentRect.top) * (deltaY / parentRect.height);
		const top = clamp(calculatedTop, finalY, startY);
		el.style.top = `${top}px`;
		el.classList.remove("hidden-on-load");
	};

	/**
	 * Since this animation happens near the top of the page, we also need to listen
	 * to changes to the header size, which affects the values we are calculating with.
	 */
	runAndAttachPageListeners(animateElement, {watchHeader: true});
};

const setupGirlRaisedHand = () => {
	const el = document.querySelector("#outcomes-bullet-points-girl-raised-hand");
	if (!el) return;

	const animateElement = () => {
		const rect = el.getBoundingClientRect();
		if (!rect.width) return;

		const parentEl = el.parentElement;
		const {offsetLeft} = parentEl;
		const startX = -(rect.width + offsetLeft); // *just* off screen to the left
		const finalX = 0;
		const {innerHeight: windowHeight} = window;
		const targetTop = windowHeight - rect.height; // when it sits right above the bottom of the window
		const deltaX = finalX - startX;

		const calculatedLeft = (targetTop - rect.top) * (deltaX / rect.height);
		const left = clamp(calculatedLeft, startX, finalX);
		const opacity = clamp((windowHeight - rect.top) / rect.height, 0, 1);
		el.style.left = `${left}px`;
		el.style.opacity = opacity;

		el.classList.remove("hidden-on-load");
	};

	runAndAttachPageListeners(animateElement);
};

const setupQuotes = () => {
	const parentEl = document.querySelector(".outcomes-quotes-container");
	const allQuotes = parentEl?.children;
	if (!parentEl || !allQuotes?.length) return;

	const animateElements = () => {
		const parentRect = parentEl.getBoundingClientRect();
		if (!parentRect.width) return;

		const {innerHeight: windowHeight, innerWidth: windowWidth} = window;
		const targetTop = windowHeight / 2; // halfway up the window
		const finalY = 0;
		Array.from(allQuotes)
			.slice(1) // skip the first quote
			.forEach((el, i) => {
				const top = (() => {
					/**
					 * 1000 comes from the CSS rule that turns this section into the mobile layout.
					 * This number should stay in sync with that.
					 */
					if (windowWidth <= 1000) {
						// mobile layout
						return 0;
					} else {
						// desktop layout

						// (1 + i) because the later callouts move faster
						// 1.1 because the quote icon sticks out the top a bit, so the "real" height is slightly taller
						const startY = parentRect.height * (1 + i) * 1.1;
						const deltaY = finalY - startY;

						const calculatedTop =
							(targetTop - parentRect.top) * (deltaY / parentRect.height);
						return clamp(calculatedTop, finalY, startY);
					}
				})();
				el.style.top = `${top}px`;

				parentEl.classList.remove("hidden-on-load");
			});
	};

	runAndAttachPageListeners(animateElements);
};

const setupReports = () => {
	const reports = document.querySelectorAll(".outcomes-reports-report");
	if (!reports?.length) return;

	const shrinkClass = "--shrink-on-narrow";
	const animateElement = i => {
		const report = reports[i];
		const card = report.querySelector(".outcomes-reports-card");
		card.style.maxHeight = report.classList.contains(shrinkClass)
			? ""
			: card.scrollHeight + "px";
	};
	const onClick = i => {
		const report = reports[i];
		report.classList.toggle(shrinkClass);
		animateElement(i);
	};

	// index of the expanded report, if there is one (we only allow one open at a time)
	reports.forEach((report, key) => {
		const readMoreButton = report.querySelector(".outcomes-reports-read-more");
		const expandButton = report.querySelector(".outcomes-reports-show-hide");

		[readMoreButton, expandButton].forEach(button => {
			button.addEventListener("click", () => onClick(key));
		});
		window.addEventListener("resize", () => animateElement(key));
	});
};

const setupOutcomesPage = () => {
	setupRocket();
	setupGirlRaisedHand();
	setupQuotes();
	setupReports();
	setupScheduleDemoModal();
};

module.exports = {
	setupOutcomesPage,
};
